import * as d3 from 'd3';

import { getRandomInt32 } from '../../../../common/helpers/getRandom';
import { CategoricalChartData } from '../CategoricalChart/CategoricalChart.model';

export class YAxis {
    private _scale: d3.scale.Ordinal<any, any>;
    private _axis: d3.svg.Axis;
    private _axisGroup: d3.Selection<any>;
    private _padding: number = 0.5;
    private _width: number;
    private _height: number;
    private _clipPathId: string;

    constructor(container: d3.Selection<SVGGElement>, width: number, height: number) {
        this._clipPathId = `labelsClipPath${getRandomInt32()}`;
        this.init(container, width, height, this._clipPathId);
        this.width(width)
            .height(height);
    }

    private init(container: d3.Selection<SVGGElement>, width: number, height: number, clipPathId: string) {
        const scale = d3.scale.ordinal();
        // .rangeBands([0, width], this._padding);
        const axis = d3.svg.axis()
            .tickFormat(d => {
                return d.label;
            })
            .scale(scale)
            .orient('left');
        const clippath = container.append('clipPath')
            .attr('id', clipPathId);
        const axisGroup = container.append('g')
            .classed('axis', true);
        clippath.append('rect')
            //.attr('x', -band / 2)
            // .attr('width', band)
            .attr('height', 20);
        this._scale = scale;
        this._axis = axis;
        this._axisGroup = axisGroup;
    }

    spacing(): number;
    spacing(value: number): this;
    spacing(value?: number): this | number {
        if (!arguments.length) {
            return this._padding;
        }
        this._padding = value;
        return this;
    }

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (!arguments.length) {
            return this._height;
        }
        this._height = value;
        return this;
    }

    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (!arguments.length) {
            return this._width;
        }
        this._width = value;
        return this;
    }

    position(value: CategoricalChartData): number {
        return this._scale(value.id);
    }

    index(position: number): number {
        const bandWidth = this._scale.rangeBand();
        const ranges = this._scale.range()
            .map(d => { return [d - bandWidth / 2, d + 3 * bandWidth / 2]; });
        const matching = ranges.filter(r => r[0] <= position && r[1] > position);
        let idx = 0;
        if (matching.length) {
            idx = ranges.indexOf(matching[0]);
        } else {
            if (d3.max(ranges, m => m[1]) < position) {
                idx = ranges.length - 1;
            }
        }
        return idx;
    }

    value(position: number): any {
        return this._scale.domain()[this.index(position)];
    }

    band(): number {
        return this._scale.rangeBand();
    }

    domain(): any[] {
        return this._scale.domain();
    }

    range(): any[] {
        return this._scale.range();
    }

    render(data: CategoricalChartData[]): void {
        const band = this.band();
        d3.select('#' + this._clipPathId)
            .select('rect')
            .attr('transform', `translate(${-this.width()},${-band / 2})`)
            .attr('height', Math.ceil(band))
            .attr('width', this.width());
        this._scale
            .rangeBands([0, this._height], this._padding)
            .domain(data.map(d => d.id));
        this._axisGroup
            .attr('transform', `translate(${0},${0})`)
            .call(this._axis);
        const ticks = this._axisGroup.selectAll('.tick');
        ticks.select('text')
            .classed('tickLabel', true);
        ticks.style('clip-path', `url('#${this._clipPathId}')`)
            .data(data)
            .classed('clickable', d => !!d.clickCallback)
            .on('click', (d, i) => {
                d.clickCallback.bind(d);
                d.clickCallback();
            })
            .each(function (d, i) {
                const found = d;
                if (found) {
                    d3.select(this).attr(found.attributes);
                }
            })
            .select('text')
            .text(d => d.label);
    }
}